import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import Alert from "react-s-alert";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import * as NavigationActions from "../../actions/navigation";
import * as SearchActions from "../../actions/search";
import {
    InlineLoading,
    NotificationAlert,
} from "../../components/common/index";
import { TextFormInput } from "../../components/form/index";
import {
    RestrictedLayout,
    TruncatedCell,
} from "../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../constants/reactTableConstants";
import * as Dtos from "../../dtos/Tmd.dtos";
import { RequestState } from "../../enumerations/RequestState";
import * as AuthenticationHelper from "../../helpers/authentication";
import { SearchBreadcrumbs } from "../../helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "../../helpers/date";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../helpers/formHelper";
import { findLookup, findLookupValue } from "../../helpers/lookupHelper";
import useNavigate from "../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../reducers/index";
import { IRegistrationFormState } from "../../reducers/reactReduxForms/index";
import FilterComponent from "./FilterComponent";

import "./SearchPage.scss";

const reduxFormName: string = "reduxForms.searchResult";

interface SearchPageProps {
    permissions?: Dtos.PersonnelTmdPermissions[];
    form: Dtos.SearchResult & IRegistrationFormState;
    formProperties: Dtos.FormProperty[];
    lookups: Dtos.Lookup[];
    validationFailures: Dtos.ResponseError[];
    searchResult: Dtos.SearchResult;
    searching: boolean;
    searchSuccess: boolean;
    searchFailure: boolean;
    clearSearch: SearchActions.ISearchClearActionCreator;
    search: SearchActions.ISearchActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const SearchPage: FC<SearchPageProps> = ({
    permissions,
    form,
    formProperties,
    lookups,
    validationFailures,
    searchResult,
    searching,
    searchSuccess,
    searchFailure,
    clearSearch,
    search,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);
    const [filtersIsOpen, setFiltersIsOpen] = useState<boolean>(false);
    const [filterOptions, setFilterOptions] = useState<string[]>([]);
    const [selectedFilterOptions, setSelectedFilterOptions] = useState<string[]>(filterOptions);

    //
    // -- Hooks --
    const navigate = useNavigate();

    // Load form
    const loadReduxForm = () => {
        let extraData: Dtos.SearchResult = {} as Dtos.SearchResult;

        loadForm(reduxFormName, Object.assign({ ...searchResult }, extraData));
    };


    // Set filter options
    useEffect(() => {
        let options = [];

        if (AuthenticationHelper.hasPermission(permissions, Dtos.Permission.TrialView, null, null)) {
            options.push("Trials");
        }
        if (AuthenticationHelper.hasPermission(permissions, Dtos.Permission.ProjectView, null, null)) {
            options.push("Projects");
        }
        if (AuthenticationHelper.hasPermission(permissions, Dtos.Permission.InstitutionView, null, null)) {
            options.push("Institutions");
        }
        if (AuthenticationHelper.hasPermission(permissions, Dtos.Permission.LabView, null, null)) {
            options.push("Labs");
        }
        if (AuthenticationHelper.hasPermission(permissions, Dtos.Permission.PersonnelView, null, null)) {
            options.push("Personnel");
        }
        if (AuthenticationHelper.hasPermission(permissions, Dtos.Permission.CommitteeView, null, null)) {
            options.push("Committees");
        }
        if (AuthenticationHelper.hasPermission(permissions, Dtos.Permission.GroupView, null, null)) {
            options.push("Groups");
        }
        if (AuthenticationHelper.hasPermission(permissions, Dtos.Permission.HrecView, null, null)) {
            options.push("HREC");
        }
        if (AuthenticationHelper.hasPermission(permissions, Dtos.Permission.HealthDistrictView, null, null)) {
            options.push("Health Districts");
        }

        setFilterOptions(options);
        setSelectedFilterOptions(options);
    }, [permissions, setFilterOptions]);

    // Set breadcrumbs and drawer items
    useEffect(() => {
        if (!initalised) {
            loadReduxForm();

            setDrawerItems([]);
            setBreadcrumbs(SearchBreadcrumbs());

            setInitalised(true);
        }
    }, [
        loadReduxForm,
        setDrawerItems,
        setBreadcrumbs,
        SearchBreadcrumbs,
        initalised,
        setInitalised,
    ]);

    // Cleanup effect to clear the search on component unmount
    useEffect(() => {
        return () => {
            clearSearch();
            resetForm(reduxFormName);
        };
    }, [clearSearch, resetForm, reduxFormName]);

    // Failed search
    useEffect(() => {
        if (!searching && searchFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={<div>Failed to search: '{searchResult.searchFor}'.</div>}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [searching, searchFailure]);

    const handleOnSubmit = async (data: any) => {
        if (data?.searchFor) {
            await search(data?.searchFor, selectedFilterOptions);
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.LaboratoryPersonnel) => {
        Alert.error(
            <NotificationAlert
                alertContent={<div>Failed to search: '{searchResult.searchFor}'.</div>}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const hasResults = useMemo(() => {
        if (
            !searching && searchResult && 
            (searchResult?.trials && searchResult.trials.length > 0) ||
            (searchResult?.projects && searchResult.projects.length > 0) ||
            (searchResult?.institutions && searchResult.institutions.length > 0) ||
            (searchResult?.laboratories && searchResult.laboratories.length > 0) ||
            (searchResult?.personnel && searchResult.personnel.length > 0) ||
            (searchResult?.committees && searchResult.committees.length > 0) ||
            (searchResult?.groups && searchResult.groups.length > 0) ||
            (searchResult?.hrecs && searchResult.hrecs.length > 0) ||
            (searchResult?.healthDistricts && searchResult.healthDistricts.length > 0)
        ) {
            return true;
        }
        return false;
    }, [searching, searchResult, clearSearch]);

    const renderMessage = useMemo(() => {
        if (!hasResults && searchSuccess) {
            return (
                <>
                    <br />
                    <div className="card card-shadow p-3">
                        <div className="alert alert-info d-flex mb-0">
                            <div className="mr-2">
                                <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                            </div>
                            <span>Nothing found.</span>
                        </div>
                    </div>
                </>
            );
        }
    }, [searching, searchResult, hasResults, searchSuccess]);

    const renderTrials = useCallback(
        (trials: Dtos.Trial[]) => {
            const showPagination = trials?.length > DEFAULT_PAGE_SIZE ? true : false;

            return (
                <ReactTable
                    data={trials}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? DEFAULT_PAGE_SIZE : trials.length}
                    pageSize={showPagination ? undefined : trials.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="Nothing found"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                navigate(`/trials/${rowInfo.original.id}/details`);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.TRIAL} fixedWidth />
                            ),
                        },
                        {
                            id: "studyAcronym",
                            Header: "Name",
                            accessor: "studyAcronym",
                            maxWidth: 200,
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                        {
                            id: "protocolID",
                            Header: "Protocol ID",
                            accessor: "protocolID",
                            maxWidth: 500,
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                        {
                            id: "shortTitle",
                            Header: "Short Title",
                            accessor: "shortTitle",
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                        {
                            id: "trialStatusId",
                            Header: "Status",
                            accessor: "trialStatusId",
                            maxWidth: 180,
                            Cell: (props) => (
                                <div>
                                    {findLookupValue("Trial", "TrialStatusId", props.value, lookups)}
                                </div>
                            ),
                        },
                    ]}
                />
            );
        },
        [
            navigate,
            PAGE_SIZE_OPTIONS,
            DEFAULT_PAGE_SIZE,
            lookups,
            findLookupValue,
        ]
    );

    const renderProjects = useCallback(
        (projects: Dtos.Project[]) => {
            const showPagination = projects?.length > DEFAULT_PAGE_SIZE ? true : false;

            return (
                <ReactTable
                    data={projects}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? DEFAULT_PAGE_SIZE : projects.length}
                    pageSize={showPagination ? undefined : projects.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="Nothing found"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_0e, handleOriginal) => {
                                navigate(`/projects/${rowInfo.original.id}/details`);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.CLIPBOARD} fixedWidth />
                            ),
                        },
                        {
                            id: "projectDateSubmitted",
                            Header: "Submitted to BCT",
                            accessor: "projectDateSubmitted",
                            maxWidth: 180,
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(props.value)}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "projectShortTitle",
                            Header: "Short Title",
                            accessor: "projectShortTitle",
                            maxWidth: 280,
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                        {
                            id: "projectScientificTitle",
                            Header: "Scientific Title",
                            accessor: "projectScientificTitle",
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                    ]}
                />
            );
        },
        [
            navigate,
            PAGE_SIZE_OPTIONS,
            DEFAULT_PAGE_SIZE,
            convertToApproximateDateString,
            convertToShortDateString,
        ]
    );

    const renderInstitutions = useCallback(
        (institutions: Dtos.Institution[]) => {
            const showPagination = institutions?.length > DEFAULT_PAGE_SIZE ? true : false;

            return (
                <ReactTable
                    data={institutions}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? DEFAULT_PAGE_SIZE : institutions.length}
                    pageSize={showPagination ? undefined : institutions.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="Nothing found"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                navigate(`/institutions/${rowInfo.original.id}/details`);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.INSTITUTION} fixedWidth />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Name",
                            accessor: "name",
                            Cell: (props) => (
                                <TruncatedCell value={props.value} />
                            ),
                        },
                        {
                            id: "code",
                            Header: "Code",
                            accessor: "code",
                            maxWidth: 90,
                        },
                        {
                            id: "institutionType",
                            Header: "Type",
                            accessor: "institutionType.value",
                            maxWidth: 180,
                        },
                        {
                            id: "country",
                            Header: "Country",
                            accessor: "country.value",
                            maxWidth: 180,
                        },
                        {
                            id: "state",
                            Header: "State",
                            accessor: "state.value",
                            maxWidth: 220,
                        },
                    ]}
                />
            );
        },
        [navigate, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE]
    );

    const renderPersonnel = useCallback(
        (personnel: Dtos.Personnel[]) => {
            const showPagination = personnel?.length > DEFAULT_PAGE_SIZE ? true : false;

            return (
                <ReactTable
                    data={personnel}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? DEFAULT_PAGE_SIZE : personnel.length}
                    pageSize={showPagination ? undefined : personnel.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="Nothing found"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_0e, handleOriginal) => {
                                navigate(`/personnel/${rowInfo.original.id}/details`);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.USER} fixedWidth />
                            ),
                        },
                        {
                            id: "salutationId",
                            Header: "Salutation",
                            accessor: "salutation.value",
                            maxWidth: 100,
                        },
                        {
                            id: "firstName",
                            Header: "First Name",
                            accessor: "firstName",
                            maxWidth: 250,
                        },
                        {
                            id: "lastName",
                            Header: "Last Name",
                            accessor: "lastName",
                            maxWidth: 250,
                        },
                        {
                            id: "email",
                            Header: "Email",
                            accessor: "email",
                        },
                        {
                            id: "organisation",
                            Header: "Organisation",
                            accessor: "personnelAddresses",
                            Cell: (props) => (
                                <span>
                                    {props.original?.personnelAddresses
                                        ?.filter((x) => x.addressTypeId == 2)
                                        ?.map((x) => x.companyName)
                                        .join(" | ")}
                                </span>
                            ),
                        },
                    ]}
                />
            );
        },
        [navigate, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE]
    );

    const renderLaboratories = useCallback(
        (laboratories: Dtos.Laboratory[]) => {
            const showPagination = laboratories?.length > DEFAULT_PAGE_SIZE ? true : false;

            return (
                <ReactTable
                    data={laboratories}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? DEFAULT_PAGE_SIZE : laboratories.length}
                    pageSize={showPagination ? undefined : laboratories.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="Nothing found"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_0e, handleOriginal) => {
                                navigate(`/labs/${rowInfo.original.id}/details`);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.FLASKVIAL} fixedWidth />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Name",
                            accessor: "name",
                        },
                        {
                            id: "suburb",
                            Header: "Suburb",
                            accessor: "suburb",
                            maxWidth: 220,
                        },
                        {
                            id: "state",
                            Header: "State",
                            accessor: "state.value",
                            maxWidth: 220,
                        },
                        {
                            id: "country",
                            Header: "Country",
                            accessor: "country.value",
                            maxWidth: 220,
                        },
                    ]}
                />
            );
        },
        [navigate, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE]
    );

    const renderCommittees = useCallback(
        (committees: Dtos.Committee[]) => {
            const showPagination =
                committees?.length > DEFAULT_PAGE_SIZE ? true : false;

            return (
                <ReactTable
                    data={committees}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? DEFAULT_PAGE_SIZE : committees.length}
                    pageSize={showPagination ? undefined : committees.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="Nothing found"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_0e, handleOriginal) => {
                                navigate(`/committees/${rowInfo.original.id}/details`);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.COMMITTEE} fixedWidth />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Committee Name",
                            accessor: "name",
                        },
                        {
                            id: "rank",
                            Header: " ",
                            accessor: "rank",
                            maxWidth: 0,
                            Cell: (_props) => <></>,
                        },
                    ]}
                />
            );
        },
        [navigate, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE]
    );

    const renderGroups = useCallback(
        (groups: Dtos.Group[]) => {
            const showPagination =
                groups?.length > DEFAULT_PAGE_SIZE ? true : false;

            return (
                <ReactTable
                    data={groups}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? DEFAULT_PAGE_SIZE : groups.length}
                    pageSize={showPagination ? undefined : groups.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="Nothing found"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_0e, handleOriginal) => {
                                navigate(`/groups/${rowInfo.original.id}/details`);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.GROUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Group Name",
                            accessor: "name",
                        },
                        {
                            id: "isDefault",
                            Header: "Default",
                            accessor: "isDefault",
                            maxWidth: 180,
                            Cell: (props) => {
                                return (
                                    <>
                                        <span className="truncate">
                                            {props.value == "" ? (
                                                ""
                                            ) : (
                                                <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Light.CHECK} fixedWidth />
                                            )}
                                        </span>
                                    </>
                                );
                            },
                        },
                    ]}
                />
            );
        },
        [navigate, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE]
    );

    const renderHrecs = useCallback(
        (hrecs: Dtos.Hrec[]) => {
            const showPagination = hrecs?.length > DEFAULT_PAGE_SIZE ? true : false;

            return (
                <ReactTable
                    data={hrecs}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? DEFAULT_PAGE_SIZE : hrecs.length}
                    pageSize={showPagination ? undefined : hrecs.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="Nothing found"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_0e, handleOriginal) => {
                                navigate(`/settings/hrecs/${rowInfo.original.id}/details`);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.HANDHOLDINGHEART} fixedWidth />
                            ),
                        },
                        {
                            id: "fullName",
                            Header: "Name",
                            accessor: "fullName",
                            Cell: (props) => (
                                <span>
                                    {props.value ?? props?.original?.fullName}
                                </span>
                            ),
                        },
                        {
                            id: "hrecNo",
                            Header: "No.",
                            accessor: "hrecNo",
                            maxWidth: 120,
                        },
                        {
                            id: "administrator.displayWithSalutation",
                            Header: "Administrator",
                            accessor: "administrator.displayWithSalutation",
                            maxWidth: 300,
                            Cell: (props) => (
                                <span>{`${
                                    props?.original?.administrator?.lastName
                                        ? props?.original?.administrator?.lastName + ","
                                        : ""
                                } ${
                                    props?.original?.administrator?.salutation?.value ?? ""
                                } ${
                                    props?.original?.administrator?.firstName ?? ""
                                }`}</span>
                            ),
                        },
                    ]}
                />
            );
        },
        [navigate, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE]
    );

    const renderHealthDistricts = useCallback(
        (healthDistricts: Dtos.HealthDistrict[]) => {
            const showPagination = healthDistricts?.length > DEFAULT_PAGE_SIZE ? true : false;

            return (
                <ReactTable
                    data={healthDistricts}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? DEFAULT_PAGE_SIZE : healthDistricts.length}
                    pageSize={showPagination ? undefined : healthDistricts.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="Nothing found"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_0e, handleOriginal) => {
                                navigate(`/settings/healthdistricts/${rowInfo.original.id}/details`);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.HOSPITALS} fixedWidth />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Name",
                            accessor: "name",
                            Cell: (props) => (
                                <span>
                                    {props.value ?? props?.original?.name}
                                </span>
                            ),
                        },
                        {
                            id: "stateId",
                            Header: "State",
                            accessor: "stateId",
                            maxWidth: 300,
                            Cell: (props) => (
                                <span>
                                    {findLookup("HealthDistrict", "StateId", lookups)?.values?.find((x) => x.id == props.value)?.value}
                                </span>
                            ),
                        },
                    ]}
                />
            );
        },
        [navigate, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE, findLookup, lookups]
    );

    const renderTable = useMemo(() => {
        return (
            <>
                {searchResult &&
                    searchResult.trials &&
                    searchResult.trials.length > 0 &&
                    selectedFilterOptions.includes("Trials") && (
                        <div key={"trials"}>
                            <br />
                            <h1>Trials</h1>
                            <div className="card card-shadow">
                                {renderTrials(searchResult.trials)}
                            </div>
                        </div>
                    )}

                {searchResult &&
                    searchResult.projects &&
                    searchResult.projects.length > 0 &&
                    selectedFilterOptions.includes("Projects") && (
                        <div key={"projects"}>
                            <br />
                            <h1>Projects</h1>
                            <div className="card card-shadow">
                                {renderProjects(searchResult.projects)}
                            </div>
                        </div>
                    )}

                {searchResult &&
                    searchResult.institutions &&
                    searchResult.institutions.length > 0 &&
                    selectedFilterOptions.includes("Institutions") && (
                        <div key={"institutions"}>
                            <br />
                            <h1>Institutions</h1>
                            <div className="card card-shadow">
                                {renderInstitutions(searchResult.institutions)}
                            </div>
                        </div>
                    )}

                {searchResult &&
                    searchResult.laboratories &&
                    searchResult.laboratories.length > 0 &&
                    selectedFilterOptions.includes("Labs") && (
                        <div key={"laboratories"}>
                            <br />
                            <h1>Labs</h1>
                            <div className="card card-shadow">
                                {renderLaboratories(searchResult.laboratories)}
                            </div>
                        </div>
                    )}

                {searchResult &&
                    searchResult.personnel &&
                    searchResult.personnel.length > 0 &&
                    selectedFilterOptions.includes("Personnel") && (
                        <div key={"personnel"}>
                            <br />
                            <h1>Personnel</h1>
                            <div className="card card-shadow">
                                {renderPersonnel(searchResult.personnel)}
                            </div>
                        </div>
                    )}

                {searchResult &&
                    searchResult.committees &&
                    searchResult.committees.length > 0 &&
                    selectedFilterOptions.includes("Committees") && (
                        <div key={"committee"}>
                            <br />
                            <h1>Committee</h1>
                            <div className="card card-shadow">
                                {renderCommittees(searchResult.committees)}
                            </div>
                        </div>
                    )}

                {searchResult &&
                    searchResult.groups &&
                    searchResult.groups.length > 0 &&
                    selectedFilterOptions.includes("Groups") && (
                        <div key={"group"}>
                            <br />
                            <h1>Group</h1>
                            <div className="card card-shadow">
                                {renderGroups(searchResult.groups)}
                            </div>
                        </div>
                    )}

                {searchResult &&
                    searchResult.hrecs &&
                    searchResult.hrecs.length > 0 &&
                    selectedFilterOptions.includes("HREC") && (
                        <div key={"hrec"}>
                            <br />
                            <h1>HREC</h1>
                            <div className="card card-shadow">
                                {renderHrecs(searchResult.hrecs)}
                            </div>
                        </div>
                    )}

                {searchResult &&
                    searchResult.healthDistricts &&
                    searchResult.healthDistricts.length > 0 &&
                    selectedFilterOptions.includes("Health Districts") && (
                        <div key={"healthDistrict"}>
                            <br />
                            <h1>Health District</h1>
                            <div className="card card-shadow">
                                {renderHealthDistricts(searchResult.healthDistricts)}
                            </div>
                        </div>
                    )}
            </>
        );
    }, [
        searchResult,
        renderTrials,
        renderProjects,
        renderInstitutions,
        renderPersonnel,
        renderLaboratories,
        selectedFilterOptions,
    ]);

    const renderContent = useMemo(() => {
        const formDisabled: boolean = searching;

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            searchDetails: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <div className="form-row pt-4 pb-2 px-4">
                        <div className="col-12">
                            {renderFormControls(
                                form,
                                "searchResult",
                                {
                                    searchFor: {
                                        type: RegistrationComponent.RegistrationControl,
                                        inputType: TextFormInput,
                                        inputDisabled: formDisabled,
                                        inputProps: {
                                            label: " ",
                                            placeholder: "Search keywords...",
                                            addOnRightButton: (
                                                <button
                                                    className="btn btn-secondary"
                                                    type="submit"
                                                    id="button-addon2"
                                                    style={{
                                                        padding: "4px 10px",
                                                    }}
                                                >
                                                    Submit
                                                </button>
                                            ),
                                        },
                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                        labelClassName: "col-form-label col-sm-12 col-md-1 d-none",
                                        fieldClassName: "col-sm-12 col-md-12 generic-form-field",
                                    },
                                },
                                lookups,
                                validationFailures,
                                formProperties,
                                changeForm
                            )}
                        </div>
                    </div>
                ),
            },
        };

        return (
            <>
                <div className="card card-shadow">
                    <div className="form-row">
                        <div className="col">
                            <Form
                                model={reduxFormName}
                                onSubmit={(val, _event) => handleOnSubmit(val)}
                                onSubmitFailed={(val) =>
                                    handleOnSubmitFailed(val)
                                }
                            >
                                {renderFormControls(
                                    form,
                                    "searchResult",
                                    formComponents,
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </Form>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="col-sm-12 col-xl px-4 pb-3">
                            <FilterComponent
                                filterOptions={filterOptions}
                                selectedFilterOptions={selectedFilterOptions}
                                setSelectedFilterOptions={setSelectedFilterOptions}
                            />
                        </div>
                        <div className="col-sm-12 col-xl-2 d-flex justify-content-sm-start justify-content-xl-end px-4 pb-3">
                            <button
                                className="text-button"
                                onClick={() => {
                                    resetForm(reduxFormName);
                                    clearSearch();
                                    setSelectedFilterOptions(filterOptions);
                                }}
                                disabled={!hasResults}
                                style={{
                                    cursor: !hasResults ? "not-allowed" : "pointer",
                                }}
                            >
                                Clear all
                            </button>
                        </div>
                    </div>
                </div>
                {!searching ? (
                    <>
                        {renderMessage}
                        {renderTable}
                    </>
                ) : (
                    <InlineLoading absoluteCenter={true} size={50} />
                )}
            </>
        );
    }, [
        searching,
        form,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        hasResults,
        filtersIsOpen,
        setFiltersIsOpen,
    ]);

    return (
        <RestrictedLayout 
            loading={false}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<SearchPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    let searchResult: Dtos.SearchResult | undefined = !(
        state.search.formData instanceof Array
    )
        ? state.search.formData
        : undefined;

    return {
        permissions: permissions,
        searchResult: searchResult!,
        searching: state.search.loadState && state.search.loadState.status == RequestState.Pending,
        searchSuccess: state.search.loadState && state.search.loadState.status == RequestState.Success,
        searchFailure: state.search.loadState && state.search.loadState.status == RequestState.Failure,
        lookups: state.search.lookups,
        form: state.reduxForms.searchResult,
        formProperties: state.search.formProperties,
        validationFailures: searchResult
            ? state.search.validationFailures
            : undefined,
        
    };
};

const mapDispatchToProps = (dispatch): Partial<SearchPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        search: bindActionCreators(SearchActions.Search, dispatch),
        clearSearch: bindActionCreators(SearchActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchPage);
